import Image from "next/image";
import { Database } from "lucide-react";
import type { CSSProperties } from "react";

import { DATA_LABEL_CLS } from "./theme";
import { EDITORIAL_LIGHT_TOKENS } from "./tokens";
import { EvidenceRiser } from "./glass-twin/evidence-riser";

const EVIDENCE_SOURCE_TOKENS = EDITORIAL_LIGHT_TOKENS;

type EvidenceSource =
  | { name: string; icon: string }
  | { name: string; databaseColor: string };

type EvidenceFamily = {
  label: string;
  sources: readonly EvidenceSource[];
};

/**
 * Six evidence families align with the landing-page Twin exhibit. This is the
 * approved marketing connector set, kept in lockstep with the connector FAQ.
 */
const EVIDENCE_FAMILIES = [
  {
    label: "Accounting · payments",
    sources: [
      { name: "Xero", icon: "xero.svg" },
      { name: "SunSystems", icon: "sunsystems.svg" },
      { name: "Stripe", icon: "stripe.svg" },
      { name: "GoCardless", icon: "gocardless.svg" },
    ],
  },
  {
    label: "Documents · files",
    sources: [
      { name: "Microsoft 365", icon: "microsoft365.svg" },
      { name: "SharePoint", icon: "sharepoint.svg" },
      { name: "Google Drive", icon: "googledrive.svg" },
    ],
  },
  {
    label: "Knowledge · CRM",
    sources: [
      { name: "Google Docs", icon: "googledocs.svg" },
      { name: "Google Sheets", icon: "googlesheets.svg" },
      { name: "Notion", icon: "notion.svg" },
      { name: "HubSpot", icon: "hubspot.svg" },
    ],
  },
  {
    label: "Email · calendar",
    sources: [
      { name: "Gmail", icon: "gmail.png" },
      { name: "Calendar", icon: "googlecalendar.png" },
      { name: "Outlook", icon: "outlook.svg" },
    ],
  },
  {
    label: "Chat · meetings",
    sources: [
      { name: "Slack", icon: "slack.png" },
      { name: "Teams", icon: "teams.svg" },
      { name: "Granola", icon: "granola.png" },
    ],
  },
  {
    label: "Engineering · data",
    sources: [
      { name: "GitHub", icon: "github.png" },
      { name: "Linear", icon: "linear.svg" },
      { name: "Postgres", databaseColor: "#336791" },
      { name: "MySQL", databaseColor: "#4479A1" },
      { name: "SQL Server", databaseColor: "#CC2927" },
    ],
  },
] as const satisfies readonly EvidenceFamily[];

export function EvidenceSourcesGrid({
  showRiser = false,
}: {
  showRiser?: boolean;
}) {
  return (
    <div
      className="relative border border-[var(--rule)] bg-[color-mix(in_srgb,var(--theme-ground)_86%,transparent)]"
      style={EVIDENCE_SOURCE_TOKENS}
    >
      {showRiser ? <EvidenceRiser /> : null}
      <div className="flex min-h-[2.65rem] items-center justify-between gap-4 border-b border-dotted border-[var(--rule)] px-4 py-[0.7rem] max-md:flex-col max-md:items-start max-md:gap-1">
        <span
          className={`text-[color-mix(in_srgb,var(--theme-ink)_80%,transparent)] ${DATA_LABEL_CLS}`}
        >
          Connected evidence sources
        </span>
        <strong
          className={`text-[var(--theme-accent-text)] ${DATA_LABEL_CLS}`}
        >
          Your source systems remain authoritative
        </strong>
      </div>
      <div className="grid grid-cols-6 gap-px bg-[var(--rule-soft)] max-lg:grid-cols-3 max-md:grid-cols-2">
        {EVIDENCE_FAMILIES.map((family) => (
          <div
            className="min-w-0 bg-[color-mix(in_srgb,var(--theme-ground)_94%,#fff)] px-4 py-3 max-md:p-3"
            key={family.label}
          >
            <span
              className={`block min-h-[2.1rem] text-[color-mix(in_srgb,var(--theme-ink)_80%,transparent)] ${DATA_LABEL_CLS}`}
            >
              {family.label}
            </span>
            <div className="mt-2 flex min-h-[8rem] flex-col justify-start gap-2">
              {family.sources.map((source) => (
                <div
                  className="flex min-w-0 items-center gap-2"
                  key={source.name}
                >
                  {"icon" in source ? (
                    <Image
                      src={`/concepts/source-icons/${source.icon}`}
                      width={26}
                      height={26}
                      alt=""
                      loading="lazy"
                      className="size-[26px] shrink-0 object-contain"
                    />
                  ) : (
                    <span
                      className="flex size-[26px] shrink-0 items-center justify-center rounded-[5px]"
                      style={{
                        backgroundColor: `color-mix(in srgb, ${source.databaseColor} 14%, transparent)`,
                        color: source.databaseColor,
                      }}
                      aria-hidden="true"
                    >
                      <Database className="size-4" strokeWidth={1.7} />
                    </span>
                  )}
                  <span className="overflow-hidden text-ellipsis whitespace-nowrap text-[0.6rem] font-semibold tracking-[0.1em] text-[color-mix(in_srgb,var(--theme-ink)_80%,transparent)] uppercase">
                    {source.name}
                  </span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      <div className="flex min-h-[2.2rem] items-center gap-2 border-t border-dotted border-[var(--rule)] px-4 py-[0.55rem]">
        <span
          className="flex size-[18px] shrink-0 items-center justify-center border border-dashed border-[var(--rule)] text-[0.65rem] font-semibold text-[color-mix(in_srgb,var(--theme-ink)_66%,transparent)]"
          aria-hidden="true"
        >
          +
        </span>
        <span
          className={`text-[color-mix(in_srgb,var(--theme-ink)_80%,transparent)] ${DATA_LABEL_CLS}`}
        >
          Further sources scoped per activation
        </span>
      </div>
    </div>
  );
}
